// client/src/coms/components/sidebar_parts/sidebarUtils.js
export function clampMenuPos(x, y, w, h) {
  try {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 99999;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 99999;

    let nx = x;
    let ny = y;

    if (nx + w > vw - 8) nx = Math.max(8, vw - w - 8);
    if (ny + h > vh - 8) ny = Math.max(8, vh - h - 8);

    return { x: nx, y: ny };
  } catch {
    return { x, y };
  }
}

export function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

export function safeObj(v) {
  return v && typeof v === 'object' ? v : {};
}

export function pickVoiceAnimationValue(v, fallback = 'flash') {
  const next = String(
    v && typeof v === 'object'
      ? v.voiceAnimation != null
        ? v.voiceAnimation
        : v.voice_animation != null
        ? v.voice_animation
        : v.active_voice_animation != null
        ? v.active_voice_animation
        : v.animation != null
        ? v.animation
        : fallback
      : fallback
  ).trim();
  return next || fallback;
}

export function normalizeMember(m) {
  if (!m) return null;
  const id = m.user_id != null ? String(m.user_id) : m.id != null ? String(m.id) : m.userId != null ? String(m.userId) : '';
  const name = String(m.display_name || m.displayName || m.username || m.name || m.handle || '').trim();
  if (!id && !name) return null;
  const voiceAnimation = pickVoiceAnimationValue(m);
  return {
    id: id || name,
    name: name || `User ${id}`,
    display_name: String(m.display_name || m.displayName || '').trim(),
    username: String(m.username || m.name || '').trim(),
    voiceAnimation,
    voice_animation: voiceAnimation,
    active_voice_animation: voiceAnimation,
  };
}

export function getServerMembers(server) {
  const s = safeObj(server);
  const list =
    safeArray(s.members) ||
    safeArray(s.memberList) ||
    safeArray(s.users) ||
    safeArray(s.serverMembers) ||
    safeArray(s.people) ||
    [];
  const out = [];
  for (const m of list) {
    const nm = normalizeMember(m);
    if (nm) out.push(nm);
  }
  const seen = new Set();
  const deduped = [];
  for (const m of out) {
    const k = String(m.id);
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push(m);
  }
  return deduped;
}

export function normalizePresenceEntry(p) {
  if (!p) return null;

  const userId =
    p.user_id != null
      ? String(p.user_id)
      : p.userId != null
      ? String(p.userId)
      : p.id != null
      ? String(p.id)
      : p.identity != null
      ? String(p.identity)
      : '';

  const name = String(p.display_name || p.displayName || p.username || p.name || p.handle || userId || '').trim();
  if (!userId && !name) return null;

  const audioLevelRaw =
    p.audioLevel != null
      ? p.audioLevel
      : p.audio_level != null
      ? p.audio_level
      : p.level != null
      ? p.level
      : 0;

  const audioLevel = Number(audioLevelRaw);

  const isSpeakingExplicit =
    typeof p.isSpeaking === 'boolean'
      ? p.isSpeaking
      : typeof p.is_speaking === 'boolean'
      ? p.is_speaking
      : typeof p.speaking === 'boolean'
      ? p.speaking
      : null;

  const voiceAnimation = pickVoiceAnimationValue(p);

  return {
    userId: userId || name,
    name: name || `User ${userId}`,
    muted: !!p.muted,
    deafened: !!p.deafened,
    connected: p.connected == null ? true : !!p.connected,
    isSpeaking: isSpeakingExplicit != null ? !!isSpeakingExplicit : Number.isFinite(audioLevel) && audioLevel > 0.02,
    audioLevel: Number.isFinite(audioLevel) ? audioLevel : 0,
    voiceAnimation,
    voice_animation: voiceAnimation,
    active_voice_animation: voiceAnimation,
  };
}

export function getPresenceByChannel(server, voiceSession) {
  const vs = safeObj(voiceSession);
  const s = safeObj(server);

  const byChannel =
    safeObj(vs.presenceByChannel) ||
    safeObj(vs.occupancyByChannel) ||
    safeObj(vs.voicePresenceByChannel) ||
    safeObj(s.presenceByChannel) ||
    safeObj(s.voicePresenceByChannel) ||
    safeObj(s.voicePresenceByChannel) ||
    safeObj(s.voicePresence) ||
    {};

  const out = {};
  for (const [k, v] of Object.entries(byChannel)) {
    const arr = safeArray(v).map(normalizePresenceEntry).filter(Boolean);
    out[String(k)] = arr;
  }
  return out;
}

export function clampInt(n, min, max) {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  const i = Math.round(x);
  return Math.max(min, Math.min(max, i));
}

export function normalizeDbIdKey(v) {
  const m = String(v || '').match(/(\d+)$/);
  return m && m[1] ? m[1] : '';
}

export function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

export function extractNumericId(v) {
  const s = String(v || '').trim();
  if (!s) return 0;
  const m = s.match(/(\d+)$/);
  return m && m[1] ? toInt(m[1], 0) : 0;
}

export function avatarUrlForUserId(userId) {
  const id = toInt(userId, 0);
  if (!id) return '';
  return `/uploads/voiceservers/users/${id}/avatar.webp`;
}

export function getServerLogoUrl(server) {
  if (!server || typeof server !== 'object') return '';
  const v =
    server.logo_url != null
      ? server.logo_url
      : server.logoUrl != null
        ? server.logoUrl
        : server.server_logo_url != null
          ? server.server_logo_url
          : server.serverLogoUrl != null
            ? server.serverLogoUrl
            : '';
  return String(v || '').trim();
}

export function makeAcronym(name) {
  const s = String(name || '').trim();
  if (!s) return 'SRV';
  const parts = s.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 4).map((p) => p[0]).join('');
  const a = letters || s.slice(0, 4);
  return String(a).toUpperCase();
}

export function pickServerKey(server) {
  const s = safeObj(server);
  const v =
    s.id != null
      ? s.id
      : s.server_id != null
      ? s.server_id
      : s.public_id != null
      ? s.public_id
      : '';
  return String(v || '').trim();
}

export function normalizeChannel(c) {
  const o = safeObj(c);

  const kind = String(o.kind || o.type || '').trim().toLowerCase() === 'voice' ? 'voice' : 'text';

  const dbIdRaw =
    o.db_id != null
      ? o.db_id
      : o.channel_db_id != null
      ? o.channel_db_id
      : (() => {
          const raw = o.id != null ? String(o.id) : o.channel_id != null ? String(o.channel_id) : '';
          const m = raw.match(/(\d+)$/);
          return m && m[1] ? m[1] : '';
        })();

  const dbId = String(dbIdRaw || '').trim();

  const explicitId =
    o.id != null
      ? String(o.id).trim()
      : o.channel_id != null
      ? String(o.channel_id).trim()
      : '';

  const id = explicitId || dbId;

  const position =
    o.position != null
      ? toInt(o.position, 0)
      : 0;

  const isPrivate =
    o.is_private != null
      ? toInt(o.is_private, 0)
      : 0;

  const visibleToAll =
    o.visible_to_all != null
      ? toInt(o.visible_to_all, 0)
      : 1;

  const visibleRoleIdsRaw = Array.isArray(o.visible_role_ids)
    ? o.visible_role_ids
    : typeof o.visible_role_ids === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(o.visible_role_ids);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : [];

  const visibleRoleIds = Array.from(
    new Set(
      safeArray(visibleRoleIdsRaw)
        .map((x) => toInt(x, 0))
        .filter((x) => x > 0)
    )
  );

  return {
    ...o,
    id,
    db_id: dbId,
    name: String(o.name || '').trim(),
    kind,
    position,
    is_private: isPrivate,
    visible_to_all: visibleToAll,
    visible_role_ids: visibleRoleIds,
  };
}

export function channelsShallowEqual(a, b) {
  const aa = safeArray(a).map(normalizeChannel);
  const bb = safeArray(b).map(normalizeChannel);
  if (aa.length !== bb.length) return false;

  for (let i = 0; i < aa.length; i += 1) {
    const x = aa[i];
    const y = bb[i];

    if (String(x.id || '') !== String(y.id || '')) return false;
    if (String(x.db_id || '') !== String(y.db_id || '')) return false;
    if (String(x.name || '') !== String(y.name || '')) return false;
    if (String(x.kind || '') !== String(y.kind || '')) return false;
    if (String(x.position || '') !== String(y.position || '')) return false;
    if (String(x.is_private || '') !== String(y.is_private || '')) return false;
    if (String(x.visible_to_all || '') !== String(y.visible_to_all || '')) return false;
    if (String(x.archived_at || '') !== String(y.archived_at || '')) return false;
    if (String(x.category_id || '') !== String(y.category_id || '')) return false;

    const xr = safeArray(x.visible_role_ids).map((v) => String(v)).join(',');
    const yr = safeArray(y.visible_role_ids).map((v) => String(v)).join(',');
    if (xr !== yr) return false;
  }

  return true;
}